import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { unavailableEndpoints } from '../../services/api/endpoints/unavailable-dates.endpoint';
import { CreatedUnavailableDateResponse, UnavailableDate } from '../../types/unavailable-dates';

interface UnavailableDatesState {
  data: CreatedUnavailableDateResponse[];
  isLoading: boolean;
  error: string | null;
}

const initialState: UnavailableDatesState = {
  data: [],
  isLoading: false,
  error: null,
};

export const fetchUnavailableDates = createAsyncThunk(
  'unavailableDates/fetchUnavailableDates',
  async (
    {
      orgId,
      resourceId,
      startDate,
      endDate,
    }: {
      orgId: string;
      resourceId: string;
      startDate: string;
      endDate: string;
    },
    { rejectWithValue }
  ) => {
    try {
      return await unavailableEndpoints.getUnavailableDates(orgId, resourceId, startDate, endDate);
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch unavailable dates');
    }
  }
);

export const createUnavailableDate = createAsyncThunk(
  'unavailableDates/createUnavailableDate',
  async ({ orgId, data }: { orgId: string; data: UnavailableDate }, { rejectWithValue }) => {
    try {
      return await unavailableEndpoints.createUnavailableDates(orgId, data);
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to create unavailable date');
    }
  }
);

export const deleteUnavailableDate = createAsyncThunk(
  'unavailableDates/deleteUnavailableDate',
  async (
    {
      orgId,
      resourceId,
      bookedDate,
    }: {
      orgId: string;
      resourceId: string;
      bookedDate: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const success = await unavailableEndpoints.deleteUnavailableDate(
        orgId,
        resourceId,
        bookedDate
      );
      if (!success) throw new Error('Deletion failed');
      return bookedDate; // Return to filter from state
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to delete unavailable date');
    }
  }
);

const unavailableDatesSlice = createSlice({
  name: 'unavailableDates',
  initialState,
  reducers: {
    clearUnavailableDates(state) {
      state.data = [];
      state.error = null;
      state.isLoading = false;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch
      .addCase(fetchUnavailableDates.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUnavailableDates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(fetchUnavailableDates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Create
      .addCase(createUnavailableDate.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createUnavailableDate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data.push(action.payload);
      })
      .addCase(createUnavailableDate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Delete
      .addCase(deleteUnavailableDate.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteUnavailableDate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = state.data.filter(date => date.bookedDate !== action.payload);
      })
      .addCase(deleteUnavailableDate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearUnavailableDates } = unavailableDatesSlice.actions;

export const selectUnavailableDates = (state: RootState) => state.unavailableDates;

export default unavailableDatesSlice.reducer;
